// Service layer for Draft Invoice operations
export interface DraftInvoiceItem {
  id?: number
  item_id: number
  item_name: string
  item_code?: string
  description?: string
  quantity: number
  rate: number
  discount_percent?: number
  discount_amount?: number
  tax_rate_id?: number
  tax_amount?: number
  amount: number
}

export interface DraftInvoice {
  id?: number
  invoice_number?: string
  customer_name?: string
  customer_phone?: string
  transaction_date: string
  total_amount: number
  tax_amount?: number
  discount_amount?: number
  notes?: string
  status: 'draft' | 'converted' | 'cancelled'
  converted_invoice_id?: number
  createdAt?: string
  updatedAt?: string
  items?: DraftInvoiceItem[]
}

export interface CreateDraftInvoiceData {
  invoice_number?: string
  customer_name?: string
  customer_phone?: string
  transaction_date: string
  total_amount: number
  tax_amount?: number
  discount_amount?: number
  notes?: string
  items: DraftInvoiceItem[]
}

// Service functions for draft invoices
export const draftInvoiceService = {
  // Create a new draft invoice
  async createDraftInvoice(
    data: CreateDraftInvoiceData
  ): Promise<{ success: boolean; id?: number; error?: string }> {
    try {
      return await window.api.db.createDraftInvoice(data)
    } catch (error) {
      console.error('Error creating draft invoice:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Get all draft invoices
  async getDraftInvoices(status?: string): Promise<DraftInvoice[]> {
    try {
      return await window.api.db.getDraftInvoices(status)
    } catch (error) {
      console.error('Error fetching draft invoices:', error)
      return []
    }
  },

  // Get a single draft invoice by ID
  async getDraftInvoice(id: number): Promise<DraftInvoice | null> {
    try {
      return await window.api.db.getDraftInvoice(id)
    } catch (error) {
      console.error('Error fetching draft invoice:', error)
      return null
    }
  },

  // Update an existing draft invoice
  async updateDraftInvoice(
    id: number,
    data: CreateDraftInvoiceData
  ): Promise<{ success: boolean; error?: string }> {
    try {
      return await window.api.db.updateDraftInvoice(id, data)
    } catch (error) {
      console.error('Error updating draft invoice:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Delete a draft invoice
  async deleteDraftInvoice(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      return await window.api.db.deleteDraftInvoice(id)
    } catch (error) {
      console.error('Error deleting draft invoice:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Convert a draft invoice to a regular invoice
  async convertDraftInvoice(
    id: number,
    convertedInvoiceId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      return await window.api.db.convertDraftInvoice(id, convertedInvoiceId)
    } catch (error) {
      console.error('Error converting draft invoice:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Calculate totals for a draft invoice
  calculateTotals(items: DraftInvoiceItem[]): {
    subtotal: number
    totalDiscount: number
    totalTax: number
    total: number
  } {
    const subtotal = items.reduce((sum, item) => sum + item.quantity * item.rate, 0)
    const totalDiscount = items.reduce((sum, item) => sum + (item.discount_amount || 0), 0)
    const totalTax = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0)
    const total = subtotal - totalDiscount + totalTax

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalDiscount: Math.round(totalDiscount * 100) / 100,
      totalTax: Math.round(totalTax * 100) / 100,
      total: Math.round(total * 100) / 100
    }
  },

  // Generate a temporary invoice number
  generateTempInvoiceNumber(): string {
    const date = new Date()
    const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '')
    const timeStr = date.getTime().toString().slice(-6)
    return `DRAFT-${dateStr}-${timeStr}`
  }
}
